import type { PromotionPreviewResultPromotionNewMembers } from '~/server_shared/openapi/data-contracts';

import { fieldPathString } from './promotionsUtils';
import type { PromotionChange } from './types';

// The scope-inventory owner adapter and its member-defining collections: a CREATE change on one of these
// collections defines a new LOB / sub-org member (mirrors the backend's new-member derivation).
const LOB_SUB_ORG_CONFIG_TYPE = 'lob_incident_configuration';
const NEW_LOB_COLLECTION = 'available_lobs';
const NEW_SUB_ORG_COLLECTION = 'sub_organizations';

/** Which member collection a new member belongs to, threaded through the interim step's toggle callback. */
export type NewMemberKind = 'lob' | 'sub_organization';

/** Whether a preview carries any new LOB / sub-org members — gates showing the interim opt-in step. */
export function hasNewMembers(newMembers?: PromotionPreviewResultPromotionNewMembers): boolean {
  return (newMembers?.lobs?.length ?? 0) + (newMembers?.sub_organizations?.length ?? 0) > 0;
}

/** Whether a change is the owner-adapter CREATE that *defines* this new member (its `available_lobs` /
 * `sub_organizations` row) — as opposed to a change that merely references the member. */
export function isNewMemberDefinitionChange(change: PromotionChange, kind: NewMemberKind, externalId: string): boolean {
  if (change.configuration_type !== LOB_SUB_ORG_CONFIG_TYPE || change.change_type !== 'CREATE') {
    return false;
  }
  const collection = kind === 'lob' ? NEW_LOB_COLLECTION : NEW_SUB_ORG_COLLECTION;

  return fieldPathString(change, 'parent_path') === collection && fieldPathString(change, 'field_id') === externalId;
}

/** The changes related to a new member — those whose scope attribution names it — excluding the member's own
 * definition CREATE. This is the blast radius the interim step shows per member ("related to N changes"): it
 * is derived from the scope attribution the preview already carries, so it approximates (not byte-exactly
 * matches) what a refine opt-out would prune or drop. */
export function relatedChangesForMember(
  changes: PromotionChange[],
  kind: NewMemberKind,
  externalId: string
): PromotionChange[] {
  return changes.filter((change) => {
    if (isNewMemberDefinitionChange(change, kind, externalId)) {
      return false;
    }
    const scoped = kind === 'lob' ? change.scope?.lobs : change.scope?.sub_organizations;

    return (scoped ?? []).includes(externalId);
  });
}

/** The locked new-member CREATE rows in a change list, split by whether their member was promoted.
 * `lockedChangeIds` are the member-defining CREATE rows whose selection is pinned to the interim step
 * (their per-change checkbox is disabled); `lockedSelectedChangeIds` is the subset whose member is promoted
 * (kept selected) — the rest are force-deselected. This pinning is what enforces "you can't keep a
 * reference while dropping the member": the member's promotion decision lives only in the step. */
export interface NewMemberLocks {
  lockedChangeIds: Set<string>;
  lockedSelectedChangeIds: Set<string>;
}

export const NO_NEW_MEMBER_LOCKS: NewMemberLocks = {
  lockedChangeIds: new Set(),
  lockedSelectedChangeIds: new Set(),
};

/** Identify the new-member CREATE rows in a diff and pin them to the step's promote/opt-out decision. A row
 * is a new-member CREATE when it CREATEs an `available_lobs` / `sub_organizations` item on the owner adapter
 * whose id is one of the preview's `new_members`. A promoted member's CREATE stays selected; an opted-out
 * member's CREATE is dropped (its references were pruned server-side by the refine re-projection). */
export function computeNewMemberLocks(
  changes: PromotionChange[],
  newMembers: PromotionPreviewResultPromotionNewMembers | undefined,
  promotedLobIds: Set<string>,
  promotedSubOrgIds: Set<string>
): NewMemberLocks {
  const lockedChangeIds = new Set<string>();
  const lockedSelectedChangeIds = new Set<string>();
  const lobIds = new Set((newMembers?.lobs ?? []).map((member) => member.external_id));
  const subOrgIds = new Set((newMembers?.sub_organizations ?? []).map((member) => member.external_id));

  for (const change of changes) {
    if (change.configuration_type !== LOB_SUB_ORG_CONFIG_TYPE || change.change_type !== 'CREATE') {
      continue;
    }
    const parentPath = fieldPathString(change, 'parent_path');
    const fieldId = fieldPathString(change, 'field_id');
    if (fieldId === undefined) {
      continue;
    }
    if (parentPath === NEW_LOB_COLLECTION && lobIds.has(fieldId)) {
      lockedChangeIds.add(change.change_id);
      if (promotedLobIds.has(fieldId)) {
        lockedSelectedChangeIds.add(change.change_id);
      }
    } else if (parentPath === NEW_SUB_ORG_COLLECTION && subOrgIds.has(fieldId)) {
      lockedChangeIds.add(change.change_id);
      if (promotedSubOrgIds.has(fieldId)) {
        lockedSelectedChangeIds.add(change.change_id);
      }
    }
  }

  return { lockedChangeIds, lockedSelectedChangeIds };
}

/** Seed a selection to "all changes selected", then apply the new-member locks: a locked row whose member
 * was opted out is removed (force-deselected). Used by the change selector when a fresh / refined preview
 * arrives. With no locks this returns the full set (unchanged behavior for the common no-new-member case). */
export function seedSelectionWithLocks(
  changeIds: string[],
  { lockedChangeIds, lockedSelectedChangeIds }: NewMemberLocks
): Set<string> {
  const next = new Set(changeIds);
  lockedChangeIds.forEach((id) => {
    if (!lockedSelectedChangeIds.has(id)) {
      next.delete(id);
    }
  });

  return next;
}
